import { useCallback, useMemo, useState } from 'react';
import { getScopeErrorMessage } from '../utils/getScopeErrorMessage';
import { buildShippingsFromRouteMonitoring } from '@/pages/Business/pages/Shippings/pages/RouteBuilder/utils/build-shippings-from-route-monitoring';
import {
  resolveVisitDisplayLatLong,
  resolveVisitShippingEntries,
} from '@/pages/Business/pages/Monitoring/utils/resolve-visit-shipping-addresses';
import {
  useRouteCardMetrics,
  useRouteDetail,
  useRouteTrajectory,
  useRouteVisitShippingEnrichment,
  useRouteVisitShippingFilters,
} from '../hooks';
import { VisitStatus } from '../enums';
import DeliveryStatusBar from './DeliveryStatusBar';
import VisitsPanel from './VisitsPanel';
import RouteTrajectoryMap from './RouteTrajectoryMap';

interface RouteCardExpandedProps {
  routeId: string;
  syncVisitSearchFromRouteShippingNumber?: string;
}

const RouteCardExpanded = ({
  routeId,
  syncVisitSearchFromRouteShippingNumber,
}: RouteCardExpandedProps) => {
  const { routeDetail, isLoading, error } = useRouteDetail({
    routeId,
    skip: false,
  });

  const { byNumber: enrichedByNumber, pendingCount } =
    useRouteVisitShippingEnrichment(routeDetail);

  const [statusFilter, setStatusFilter] = useState<VisitStatus | null>(null);

  const shippingFilters = useRouteVisitShippingFilters(
    routeDetail?.visits,
    enrichedByNumber,
    pendingCount
  );
  const { filterVisits } = shippingFilters;

  const routeCardMetrics = useRouteCardMetrics(routeDetail);

  const { trajectory } = useRouteTrajectory({ routeId });

  const [mapFocusTarget, setMapFocusTarget] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const handleVisitMapFocus = useCallback(
    (visitLatitude: number, visitLongitude: number) => {
      setMapFocusTarget({ latitude: visitLatitude, longitude: visitLongitude });
    },
    []
  );

  const handleMapFocusConsumed = useCallback(() => {
    setMapFocusTarget(null);
  }, []);

  const visitsWithCoords = useMemo(() => {
    if (!routeDetail) {
      return [];
    }
    const shippings = buildShippingsFromRouteMonitoring(routeDetail);
    const byNumber = new Map(shippings.map((s) => [s.number, s]));
    
    const filteredVisits = filterVisits(
      statusFilter
        ? routeDetail.visits.filter((v) => v.status === statusFilter)
        : routeDetail.visits
    );

    const baseVisitId = routeDetail.visits.find(
      (routeVisit) => !(routeVisit.shippings && routeVisit.shippings.length > 0)
    )?.id;

    return filteredVisits.map((visit) => {
      const shipping = byNumber.get(visit.shippingNumber);
      const shippingEntries = resolveVisitShippingEntries(
        visit,
        enrichedByNumber
      );
      const { lat: visitLatitude, long: visitLongitude } =
        resolveVisitDisplayLatLong(visit, shippingEntries, shipping);
      const primaryAddress = shippingEntries.find(
        (entry) => entry.address?.streetName
      )?.address;
      const addressText = [
        primaryAddress?.streetName ?? visit.address?.streetName,
        primaryAddress?.doorNumber ?? visit.address?.doorNumber,
        primaryAddress?.apartmentNumber ?? visit.address?.apartmentNumber,
      ]
        .filter(Boolean)
        .join(' ')
        .trim();
      return {
        latitude: String(visitLatitude),
        longitude: String(visitLongitude),
        status: visit.status,
        order: visit.order,
        recipient: visit.recipient ?? visit.recipientName ?? undefined,
        address: addressText,
        isBase: baseVisitId !== undefined && visit.id === baseVisitId,
        shippingNumber: visit.shippingNumber,
      };
    });
  }, [routeDetail, statusFilter, enrichedByNumber, filterVisits]);

  if (isLoading) {
    return (
      <div className='text-center text-gray-500 py-8'>Cargando detalles...</div>
    );
  }

  if (error) {
    return (
      <div className='text-center text-red-500 py-8'>
        {getScopeErrorMessage(error) ?? 'Error al cargar los detalles de la ruta'}
      </div>
    );
  }

  if (!routeDetail) {
    return (
      <div className='text-center text-red-500 py-8'>
        No se encontraron datos de la ruta
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6 lg:flex-row'>
      <div className='flex flex-col gap-4 lg:flex-1'>
        <VisitsPanel
          routeDetail={routeDetail}
          statusFilter={statusFilter}
          shippingFilters={shippingFilters}
          enrichedByNumber={enrichedByNumber}
          onVisitMapFocus={handleVisitMapFocus}
          syncVisitSearchFromRouteShippingNumber={
            syncVisitSearchFromRouteShippingNumber
          }
        />
      </div>

      <div className='flex min-w-0 flex-col gap-6 lg:flex-1'>
        <div className='shrink-0 overflow-x-visible'>
          <DeliveryStatusBar
            deliveredCount={routeCardMetrics.totalDelivered}
            failedCount={routeCardMetrics.totalFailed}
            pendingCount={routeCardMetrics.totalPending}
            expiredCount={routeCardMetrics.totalExpired}
            onSegmentClick={setStatusFilter}
            selectedStatus={statusFilter}
          />
        </div>

        <RouteTrajectoryMap
          trajectory={trajectory}
          visits={visitsWithCoords}
          focusTarget={mapFocusTarget}
          onFocusTargetConsumed={handleMapFocusConsumed}
          liveDriverId={routeDetail.route.driver?.id}
          liveRouteStatus={routeDetail.route.status}
          driverName={
            routeDetail.route.driver
              ? `${routeDetail.route.driver.firstName} ${routeDetail.route.driver.lastName}`
              : undefined
          }
        />
      </div>
    </div>
  );
};

export default RouteCardExpanded;
